import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LabelSheet from "@/components/LabelSheet";
import LabelPrintDialog from "@/components/LabelPrintDialog";
import { Layers, Printer, Search } from "lucide-react";

const SLIDE_TYPES = ["H&E", "Unstained", "Recut", "IHC", "Special Stain", "Frozen", "FNA"];

export default function BulkSlides() {
  const { can } = useAuth();
  const [q, setQ] = useState("");
  const [cases, setCases] = useState([]);
  const [blocksByCase, setBlocksByCase] = useState({});
  const [picked, setPicked] = useState({});
  const [slideType, setSlideType] = useState("H&E");
  const [levels, setLevels] = useState(1);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [labels, setLabels] = useState(null);
  const [printOpen, setPrintOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cases", { params: { q, limit: 60 } });
      setCases(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const toggleCase = async (c) => {
    if (picked[c.id]) {
      setPicked((p) => { const n = { ...p }; delete n[c.id]; return n; });
      return;
    }
    setPicked((p) => ({ ...p, [c.id]: { block_ids: [] } }));
    if (!blocksByCase[c.id]) {
      try {
        const { data } = await api.get(`/cases/${c.id}`);
        setBlocksByCase((b) => ({ ...b, [c.id]: data.specimens.filter((s) => s.kind === "block") }));
      } catch (e) { toast.error(apiError(e)); }
    }
  };

  const toggleBlock = (caseId, blockId) => {
    setPicked((p) => {
      const cur = p[caseId]?.block_ids || [];
      const next = cur.includes(blockId) ? cur.filter((x) => x !== blockId) : [...cur, blockId];
      return { ...p, [caseId]: { block_ids: next } };
    });
  };

  const plannedSlides = useMemo(() => Object.entries(picked).reduce((sum, [caseId, sel]) => {
    const all = blocksByCase[caseId] || [];
    const n = sel.block_ids.length || all.length;
    return sum + n * (Number(levels) || 1);
  }, 0), [picked, blocksByCase, levels]);

  const submit = async () => {
    const targets = Object.entries(picked).map(([case_id, sel]) => ({ case_id, block_ids: sel.block_ids }));
    if (!targets.length) { toast.error("Pick at least one case"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/specimens/bulk", {
        targets, slide_type: slideType, levels: Number(levels) || 1, notes,
      });
      setResult(data);
      toast.success(`Cut ${data.created_count} ${slideType} slides across ${data.per_case.length} case(s)`);
      setPicked({});
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const printTray = () => {
    if (!result) return;
    const items = result.per_case.flatMap((pc) => pc.barcodes.map((b) => ({
      id: b, barcode: b, patient_name: pc.patient_name, kind: "slide",
      slide_type: slideType, block_designation: b.split("-").slice(-3, -2)[0] || "",
      level: (b.match(/-L(\d+)-/) || [])[1] || "",
    })));
    setLabels(items);
  };

  return (
    <div className="space-y-5" data-testid="bulk-slides-page">
      {labels && <LabelSheet items={labels} onClose={() => setLabels(null)} />}
      <LabelPrintDialog open={printOpen} onOpenChange={setPrintOpen} defaultSpec="slide"
                        barcodes={(result?.per_case || []).flatMap((pc) => pc.barcodes)} />

      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Bulk Slide Entry</h1>
        <p className="mt-1 text-sm text-slate-600">
          Log a whole tray at once — pick the cases, pick blocks (or leave all), choose the stain and levels.
        </p>
      </div>

      <div className="grid min-w-0 gap-5 lg:grid-cols-[1.4fr_1fr]">
        <section className="min-w-0 border border-slate-200" data-testid="bulk-case-picker">
          <header className="border-b border-slate-200 bg-slate-50 p-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-3.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="bulk-case-search"
                placeholder="Scan or search accession / patient…" className="h-11 border-slate-300 bg-white pl-9" />
            </div>
          </header>
          <ul className="max-h-[52vh] divide-y divide-slate-100 overflow-y-auto">
            {cases.map((c) => {
              const sel = picked[c.id];
              const blocks = blocksByCase[c.id] || [];
              return (
                <li key={c.id} className="px-3 py-2" data-testid={`bulk-case-${c.accession_number}`}>
                  <label className="flex cursor-pointer items-center gap-3">
                    <input type="checkbox" checked={Boolean(sel)} onChange={() => toggleCase(c)}
                      data-testid={`bulk-case-toggle-${c.accession_number}`} className="h-5 w-5 accent-[#134e4a]" />
                    <span className="font-mono font-semibold text-[#134e4a]">{c.accession_number}</span>
                    <span className="text-sm text-slate-800">{c.patient_name}</span>
                    <span className="ml-auto text-xs text-slate-500">{c.specimen_type || "—"}</span>
                  </label>
                  {sel && (
                    <div className="mt-2 flex flex-wrap gap-2 pl-8" data-testid={`bulk-blocks-${c.accession_number}`}>
                      {blocks.length === 0 && <span className="text-xs text-amber-700">No blocks logged on this case.</span>}
                      {blocks.map((b) => {
                        const on = sel.block_ids.length === 0 || sel.block_ids.includes(b.id);
                        return (
                          <button key={b.id} type="button" onClick={() => toggleBlock(c.id, b.id)}
                            data-testid={`bulk-block-${b.barcode}`}
                            className={`px-2 py-1 text-xs font-semibold ${on ? "bg-[#134e4a] text-white" : "border border-slate-300 text-slate-600"}`}>
                            {b.block_designation || b.label}
                          </button>
                        );
                      })}
                      {blocks.length > 0 && sel.block_ids.length === 0 && (
                        <span className="self-center text-xs text-slate-500">all blocks</span>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
            {cases.length === 0 && <li className="px-3 py-8 text-center text-sm text-slate-500">No cases match.</li>}
          </ul>
        </section>

        <section className="min-w-0 space-y-3 border border-slate-200 p-3" data-testid="bulk-tray-form">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600">Slide type</Label>
            <select value={slideType} onChange={(e) => setSlideType(e.target.value)} data-testid="bulk-slide-type-select"
              className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
              {SLIDE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600">Levels per block</Label>
            <Input type="number" min="1" max="20" value={levels} onChange={(e) => setLevels(e.target.value)}
              data-testid="bulk-levels-input" className="mt-1 h-11 border-slate-300" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600">Note (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="bulk-notes-input"
              placeholder="e.g. morning tray, cut by JD" className="mt-1 h-11 border-slate-300" />
          </div>
          <div className="border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" data-testid="bulk-summary">
            <span className="num font-display text-2xl font-bold text-slate-900">{plannedSlides}</span> slide(s)
            across <span className="num font-semibold">{Object.keys(picked).length}</span> case(s)
          </div>
          {can("inventory:write") && (
            <Button onClick={submit} disabled={busy || !plannedSlides} data-testid="bulk-submit-button"
              className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
              <Layers size={15} className="mr-2" />{busy ? "Cutting…" : "Log the tray"}
            </Button>
          )}

          {result && (
            <div className="space-y-2 border border-green-800/30 bg-green-50 p-3" data-testid="bulk-result">
              <div className="text-sm font-semibold text-green-900">Logged {result.created_count} slide(s)</div>
              {result.per_case.map((pc) => (
                <div key={pc.case_id} className="text-xs text-slate-700" data-testid={`bulk-result-${pc.accession_number}`}>
                  <span className="font-mono font-semibold">{pc.accession_number}</span> — {pc.barcodes.join(", ")}
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setPrintOpen(true)} data-testid="bulk-print-printer"
                        className="h-10 flex-1 bg-[#134e4a] hover:bg-[#0f3f3c]">
                  <Printer size={14} className="mr-2" /> Label printer
                </Button>
                <Button variant="outline" onClick={printTray} data-testid="bulk-print-labels" className="h-10 flex-1">
                  <Printer size={14} className="mr-2" /> Quick sheet
                </Button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
